/**
 * useIosNotificationCleanup — clears the iOS app-icon badge and the
 * tray of delivered notifications when the user actually opens the
 * app. Without this hook a single APNS push leaves a "1" badge that
 * never goes away, because iOS only resets badges when the app
 * tells it to (the platform never assumes the user has "seen" them).
 *
 * Behaviour
 * ─────────
 * No-op on the web. On the Capacitor shells it:
 *
 *   1. Runs once on mount (cold start) — by the time React mounts,
 *      the user has clearly opened the app, so any stale banner /
 *      badge from a push that arrived while the app was closed has
 *      been seen.
 *
 *   2. Runs again on every App.appStateChange where `isActive`
 *      becomes true. Covers the warm path: app was backgrounded,
 *      a push arrived, user tapped the icon and brought the app
 *      back to the foreground.
 *
 * Both branches call FirebaseMessaging.removeAllDeliveredNotifications()
 * to wipe the system-tray banners — iOS keeps these around indefinitely
 * until the app asks for a clear.
 *
 * The icon-badge number itself is cleared natively in AppDelegate.swift
 * inside applicationDidBecomeActive — the Firebase plugin does not
 * expose a setBadge API, and going through UIApplication directly is
 * the documented iOS path.
 *
 * Errors are swallowed — this is housekeeping, never blocking.
 */
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";

async function clearAll(): Promise<void> {
  try {
    const mod = await import("@capacitor-firebase/messaging");
    await mod.FirebaseMessaging.removeAllDeliveredNotifications();
  } catch {
    // No native shell, or the plugin failed to import — nothing
    // to do on the web.
  }
}

export function useIosNotificationCleanup(): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // First run — covers cold launch.
    void clearAll();
    // Subsequent runs — every foreground transition.
    let cancelled = false;
    const handlePromise = CapacitorApp.addListener("appStateChange", (state) => {
      if (cancelled) return;
      if (state.isActive) void clearAll();
    });
    return () => {
      cancelled = true;
      handlePromise.then(h => { try { void h.remove(); } catch {} }).catch(() => {});
    };
  }, []);
}
